import React, { useState, useEffect } from 'react'
import { Form, message, Table, Badge, Tooltip, Input, Button, Drawer, Select } from 'antd'
import { useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { FaArrowLeft, FaEye } from 'react-icons/fa'
import { RiDeleteBin6Line } from 'react-icons/ri'
import { DeleteOutlined, EditOutlined, EyeInvisibleOutlined, EyeOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons'
import axios from 'axios'
import { verifyToken } from '../../utils/helpers'
import { secureApi } from '../../Config/api'
import { GET, POST } from '../../utils/apis'
import { translations } from '../../utils/constants'
import { setCurrentPage, setDevicesData, setPageSize as setDevicePageSize, setTotalDevices } from '../../Redux/actions/deviceAction'
import { DeleteModal, DeviceMap, HlsPlayer } from '../../Components'
import { ArrowDownToLine } from 'lucide-react'

const Devices = ({ language }) => {
    const dispatch = useDispatch()
    const history = useHistory()
    const user = useSelector(state => state?.authReducer?.user)
    const devicesFromRedux = useSelector(state => state.devices?.devices);
    const currentPage = useSelector(state => state.deviceReducer?.currentPage || 1);
    const [addDevice] = Form.useForm()
    const [drawerVisible, setDrawerVisible] = useState(false)
    const [editingUser, setEditingUser] = useState(null)
    const [addDataLoading, setAddDataLoading] = useState(false)
    const [initialLoading, setInitialLoading] = useState(false)
    const [downloadLoading, setDownloadLoading] = useState(false)
    const [viewDrawerVisible, setViewDrawerVisible] = useState(false)
    const [selectedDevice, setSelectedDevice] = useState(null)
    const [searchQuery, setSearchQuery] = useState('')
    const [selectedCamera, setSelectedCamera] = useState(null)
    const [pageSize, setPageSize] = useState(10)
    const [cameraLoading, setCameraLoading] = useState(false)
    const [totalItems, setTotalItems] = useState(0)
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [recordToDelete, setRecordToDelete] = useState(null)
    const [showHiddenCameras, setShowHiddenCameras] = useState(false)
    const [visibilityLoading, setVisibilityLoading] = useState(null)
    const deviceTypeOptions = [
        { label: 'Router', value: 'router' },
        { label: 'DVR', value: 'DVR' },
    ]

    useEffect(() => {
        getAllDevices()
    }, [currentPage, searchQuery])

    useEffect(() => {
        verifyToken(history, dispatch)
    }, [])

    const confirmDelete = async () => {
        if (recordToDelete) {
            await handleDelete(recordToDelete);
            setIsDeleteModalOpen(false);
            setRecordToDelete(null);
        }
    }

    const getAllDevices = async () => {
        try {
            setInitialLoading(true)

            const params = {
                page: currentPage,
                limit: pageSize,
                search: searchQuery?.trim() || "",
                user_id: user?.id,
                deviceType: ""
            }

            const response = await secureApi?.get(GET?.GET_DEVICES, { params })
            if (response?.success) {
                const mappedData = response?.data?.map((item) => ({
                    ...item,
                    key: item.id,
                    deviceName: item.device_name,
                    serialNumber: item.device_serial,
                    deviceType: item.device_type
                }))
                dispatch(setDevicesData(mappedData))
                const totalCount = response?.pagination?.totalDevices || 0;
                dispatch(setTotalDevices(Number(totalCount)));
            } else {
                dispatch(setDevicesData([]))
                message.error(response?.message || "Failed to fetch devices");
            }
        } catch (err) {
            console.error("Fetch Error:", err);
            message.error(translations[language]?.fetchError || "Error loading devices");
        } finally {
            setInitialLoading(false);
        }
    }

    const handleCameraClick = async (cam) => {
        try {
            setCameraLoading(true)

            const params = {
                camera_index_code: cam?.camera_index_code,
            }

            const response = await secureApi.get(GET?.GET_CAMERA_LIVE, { params })
            if (response?.success) {
                setSelectedCamera({
                    preview_url: response?.data?.preview_url
                })
            } else {
                message.error(response?.message === 'Failed to fetch live stream. Camera might be offline.' ? translations[language]?.failedFetchLiveStream : (response?.message || translations[language]?.unableToLoadStream))
            }
        } catch (error) {
            console.error("Camera API Error:", error)
            message.error(translations[language]?.failedFetchLiveStream)
        } finally {
            setCameraLoading(false)
        }
    }

    const handleToggleCameraVisibility = async (cam) => {
        const nextHidden = !cam?.is_hidden
        try {
            setVisibilityLoading(cam?.camera_index_code)
            const response = await secureApi.post(POST?.SET_CAMERA_VISIBILITY, {
                camera_index_code: cam?.camera_index_code,
                is_hidden: nextHidden
            })
            if (response?.success) {
                // Update the open drawer right away, then refresh the list behind it.
                setSelectedDevice(prev => prev ? {
                    ...prev,
                    camera: (prev.camera || []).map(c =>
                        c.camera_index_code === cam.camera_index_code ? { ...c, is_hidden: nextHidden } : c
                    )
                } : prev)
                message.success(nextHidden ? translations[language]?.cameraHidden : translations[language]?.cameraVisible)
                getAllDevices()
            } else {
                message.error(response?.message || translations[language]?.cameraVisibilityError)
            }
        } catch (error) {
            console.error("Camera visibility error:", error)
            message.error(translations[language]?.cameraVisibilityError)
        } finally {
            setVisibilityLoading(null)
        }
    }

    const handleAction = async (values) => {
        const isTokenValid = await verifyToken(history, dispatch);
        if (!isTokenValid) return;

        try {
            setAddDataLoading(true)
            const endpoint = editingUser ? POST?.UPDATE_DEVICE : POST?.ADD_DEVICE
            const payload = {
                ...values,
                user_id: user?.id,
                ...(editingUser && { id: editingUser.id })
            }

            const response = await secureApi.post(endpoint, payload)
            if (response.success) {
                message.success(editingUser ? translations[language]?.deviceUpdated : translations[language]?.deviceAdded)
                setDrawerVisible(false)
                addDevice.resetFields()
                getAllDevices()
            } else {
                message.error(
                    response?.error === 'No serial number found.'
                        ? translations[language]?.noSerialNumberFound
                        : (response?.error || translations[language]?.operationFailed)
                )
            }
        } catch (error) {
            message.error('Something went wrong')
        } finally {
            setAddDataLoading(false)
        }
    }

    const handleDelete = async (record) => {
        const isTokenValid = await verifyToken(history, dispatch)
        if (!isTokenValid) return
        try {
            const response = await secureApi.post(POST?.DELETE_DEVICE, { id: record })
            if (response?.success) {
                message.success(translations[language]?.deviceDeleted)
                getAllDevices()
            }
            else (
                message.error('Delete failed')
            )
        } catch (error) {
            message.error('Delete failed')
        }
    }

    const onView = (record) => {
        setSelectedDevice(record)
        setViewDrawerVisible(true)
    }

    const isAdmin = user?.role === 'admin'

    const ownerColumn = {
        title: translations?.[language]?.deviceOwner,
        key: 'owner',
        render: (_, record) => record?.owner_name || record?.owner_email ? (
            <div>
                <div>{record?.owner_name || '-'}</div>
                {record?.owner_email && <div style={{ fontSize: '12px', color: '#999' }}>{record.owner_email}</div>}
            </div>
        ) : '-'
    }

    const columns = [
        {
            title: translations?.[language]?.serialNumber,
            dataIndex: 'serialNumber',
            key: 'serialNumber',
            render: (text, record) => (
                <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <FaEye style={{ marginRight: 8, color: '#ED2328' }} onClick={() => onView(record)} />
                    <Tooltip title={text}>
                        <span className="capitalized-name">{text || '-'}</span>
                    </Tooltip>
                    <Badge
                        status={
                            record?.device_type === 'DVR'
                                ? (record?.dvr_status == 2 ? 'error' : 'success')
                                : (record?.router_status === false ? 'error' : 'success')
                        }
                        style={{ marginLeft: 8 }}
                    /></div>
            )
        },
        {
            title: translations?.[language]?.typeOfDevice,
            dataIndex: 'device_type',
            key: 'deviceType',
            render: (text) => <span className="capitalized-name">{text || '-'}</span>
        },
        {
            title: translations?.[language]?.deviceName,
            dataIndex: 'device_name',
            key: 'deviceName',
            render: (text) => text || '-'
        },
        // Which user the device belongs to: admins only.
        ...(isAdmin ? [ownerColumn] : []),
        {
            title: translations?.[language]?.action,
            key: 'action',
            render: (_, record) => (
                <div style={{ display: 'flex', gap: "10px" }}>
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => {
                            setEditingUser(record);
                            addDevice.setFieldsValue({
                                deviceType: record.device_type,
                                serialNumber: record.serialNumber,
                                deviceName: record.device_name
                            });
                            setDrawerVisible(true);
                        }}
                    />
                    <Button type="link" onClick={() => { setRecordToDelete(record.id); setIsDeleteModalOpen(true) }} danger icon={<DeleteOutlined />} />
                </div>
            )
        },
    ]

    const handleDownloadPDF = async () => {
        try {
            setDownloadLoading(true);
            const params = {
                user_id: user?.id,
                search: searchQuery?.trim() || "",
                language: language
            };

            const res = await axios.get(GET?.GET_DOWNLOAD_DEVICE_PDF, { params })
            if (res?.data?.success && res?.data?.data) {
                const pdfUrl = res.data.data;

                const link = document.createElement('a');
                link.href = pdfUrl;
                link.download = `${language === 'it' ? 'Rapporto_Dispositivi' : 'Device_Report'}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                message.success(
                    language === 'it'
                        ? 'Rapporto dispositivi scaricato con successo'
                        : 'Device report downloaded successfully'
                );
            } else {
                message.error(
                    res?.data?.message ||
                    (language === 'it'
                        ? 'Impossibile scaricare il rapporto dispositivi'
                        : 'Failed to download device report')
                );
            }
        } catch (error) {
            console.error('Device PDF download error:', error);
            message.error(
                language === 'it'
                    ? 'Errore durante il download del rapporto dispositivi'
                    : 'Error downloading device report'
            );
        } finally {
            setDownloadLoading(false);
        }
    }

    return (
        <div className='main-container' style={{ padding: 24 }}>
            <div className="home-header" style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem", alignItems: "center", marginBottom: 32 }}>
                <div>
                    <h1 className="timesheet-title">{translations?.[language]?.devices}</h1>
                    <p className="timesheet-subtitle">{translations?.[language]?.deviceSubtitle}</p>
                </div>
                <div style={{ display: "flex", gap: "1rem" }}>
                    <Input
                        placeholder={translations[language]?.searchDevices}
                        prefix={<SearchOutlined />}
                        className="antd-search-input"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{ width: 250 }}
                        allowClear
                    />
                <Button
                    type="primary"
                    className="export-btn"
                    onClick={() => {
                        setEditingUser(null);
                        addDevice.resetFields();
                        setDrawerVisible(true);
                    }}
                    >
                    <PlusOutlined style={{ height: '18px' }} className='icons' />
                    {translations?.[language]?.addDevice}
                </Button>
                    </div>
            </div>

            <div className='table-container'>
                <div className='table-header' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <div>
                        <h2 className='h1-theme-color margin-bottom-0'>{translations?.[language]?.deviceList}</h2>
                    </div>
                    <div>
                        <Button
                            type="primary"
                            loading={downloadLoading}
                            className="export-btn"
                            style={{ display: 'inline-flex', alignItems: 'center' }}
                            onClick={handleDownloadPDF}
                        >
                            <ArrowDownToLine style={{ height: '15px' }} className='icons' />
                            {translations?.[language]?.downloadPDF}
                        </Button>
                    </div>
                </div>
                <Table
                    dataSource={devicesFromRedux}
                    columns={columns}
                    rowKey="id"
                    className="custom-timesheet-table"
                    loading={initialLoading}
                    scroll={{ x: 'max-content' }}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: totalItems,
                        showSizeChanger: true,
                        showQuickJumper: true,
                    }} />
            </div>

            <Drawer
                title={editingUser ? translations[language]?.editDevice : translations[language]?.addDevice}
                width={720}
                open={drawerVisible}
                onClose={() => setDrawerVisible(false)}
                destroyOnClose
                footer={
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
                        <Button onClick={() => setDrawerVisible(false)} className="cancel-button">
                            {translations[language]?.cancel}
                        </Button>
                        <Button type="primary" loading={addDataLoading} onClick={() => addDevice.submit()} className="submit-button">
                            {editingUser ? translations[language]?.update : translations[language]?.addDevice}
                        </Button>
                    </div>
                }
            >
                <Form form={addDevice} layout='vertical' onFinish={handleAction}>
                    <Form.Item
                        label={translations?.[language]?.typeOfDevice}
                        name='deviceType'
                        rules={[{ required: true, message: translations?.[language]?.pleaseEnterTypeOfDevice }]}
                    >
                        <Select options={deviceTypeOptions} placeholder={translations[language]?.deviceType} className="antd-select" />
                    </Form.Item>
                    <Form.Item
                        label={translations[language]?.serialNumber}
                        name='serialNumber'
                        rules={[{ required: true, message: translations[language]?.pleaseEnterSerialNumber }]}
                    >
                        <Input className="antd-input" placeholder={translations[language]?.enterSerialNumber} />
                    </Form.Item>
                    <Form.Item
                        label={translations[language]?.deviceName}
                        name='deviceName'
                        rules={[{ required: true, message: translations[language]?.placeholderEnterDeviceName }]}
                    >
                        <Input className="antd-input" placeholder={translations[language]?.enterDeviceName} />
                    </Form.Item>
                </Form>
            </Drawer>
            <Drawer
                title={<span>{selectedCamera ? selectedCamera?.camera_name : translations[language]?.devices}</span>}
                width={720}
                open={viewDrawerVisible}
                onClose={() => {
                    setViewDrawerVisible(false);
                    setSelectedCamera(null);
                }}
            >
                {selectedDevice && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div style={{ padding: '20px', borderRadius: '12px', border: '1px solid #f0f0f0', backgroundColor: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                            <p style={{ margin: '4px 0' }}><strong>{translations[language]?.deviceType}:</strong> {selectedDevice?.device_type || '-'}</p>
                            <p style={{ margin: '4px 0' }}><strong>{translations[language]?.serialNumber}:</strong> {selectedDevice?.device_serial || '-'}</p>
                            <p style={{ margin: '4px 0' }}><strong>{translations[language]?.deviceName}:</strong> {selectedDevice?.device_name || '-'}</p>
                            {isAdmin && (
                                <p style={{ margin: '4px 0' }}>
                                    <strong>{translations[language]?.deviceOwner}:</strong> {selectedDevice?.owner_name || '-'}
                                    {selectedDevice?.owner_email && <span style={{ color: '#999' }}> ({selectedDevice.owner_email})</span>}
                                </p>
                            )}

                            {selectedDevice?.device_type === 'DVR' && (
                                <p style={{ margin: '4px 0' }}><strong>{translations[language]?.dvrName}:</strong> {selectedDevice?.dvr_name || '-'}</p>
                            )}

                            <div style={{ borderTop: '1px solid #eee', margin: '12px 0' }}></div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <strong>{translations[language]?.status}:</strong>
                                {(() => {
                                    const isOffline = selectedDevice?.device_type === 'DVR'
                                        ? (selectedDevice?.dvr_status === 2 || selectedDevice?.dvr_status === '2')
                                        : selectedDevice?.router_status === false

                                    return (
                                        <span style={{ color: isOffline ? '#ff4d4f' : '#52c41a', fontWeight: 'bold' }}>
                                            ● {isOffline ? translations[language]?.offline : translations[language]?.online}
                                        </span>
                                    );
                                })()}
                            </div>

                            <p style={{ fontSize: '10px', color: '#bfbfbf', marginTop: '8px' }}>
                                {translations[language]?.createdAt}: {selectedDevice?.created_at ? new Date(selectedDevice?.created_at).toLocaleDateString() : '-'}
                            </p>
                        </div>

                        {selectedDevice?.device_type === 'DVR' ? (
                            selectedCamera ? (
                                <div style={{ marginTop: '10px' }}>
                                    <div
                                        onClick={() => setSelectedCamera(null)}
                                        style={{ cursor: 'pointer', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '5px', color: '#666', fontSize: '14px' }}
                                    >
                                        <FaArrowLeft /> {translations[language]?.backToList}
                                    </div>

                                    <div style={{
                                        width: '100%',
                                        aspectRatio: '16/9',
                                        backgroundColor: 'black',
                                        borderRadius: '12px',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        overflow: 'hidden',
                                        position: 'relative'
                                    }}>
                                        {cameraLoading ? (
                                            <div style={{ color: 'white' }}>Loading Live Stream...</div>
                                        ) : selectedCamera?.preview_url ? (
                                            <HlsPlayer
                                                src={selectedCamera?.preview_url}
                                                onError={() => setSelectedCamera(prev => ({ ...prev, preview_url: null }))}
                                                style={{ width: '100%', height: '100%' }}
                                            />
                                        ) : (
                                            <div style={{ textAlign: 'center' }}>
                                                <p style={{ color: '#ff4d4f', marginBottom: '8px' }}>{translations[language]?.unableToLoadStream}</p>
                                                <button
                                                    onClick={() => handleCameraClick(selectedCamera)}
                                                    style={{ backgroundColor: '#ED2328', color: 'white', border: 'none', padding: '5px 15px', borderRadius: '4px', cursor: 'pointer' }}
                                                >
                                                    {translations[language]?.retry}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div style={{ marginTop: '10px' }}>
                                    {(() => {
                                        const allCameras = selectedDevice?.camera || []
                                        const hiddenCount = allCameras.filter(c => c?.is_hidden).length
                                        const visibleCameras = showHiddenCameras ? allCameras : allCameras.filter(c => !c?.is_hidden)
                                        return (
                                    <>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                        <h3 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>
                                            {translations[language]?.camerasList}
                                        </h3>
                                        {hiddenCount > 0 && (
                                            <Button
                                                type="link"
                                                size="small"
                                                icon={showHiddenCameras ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                                                onClick={() => setShowHiddenCameras(v => !v)}
                                            >
                                                {showHiddenCameras
                                                    ? translations[language]?.hideHiddenCameras
                                                    : `${translations[language]?.showHiddenCameras} (${hiddenCount})`}
                                            </Button>
                                        )}
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                        {visibleCameras.length === 0 && (
                                            <div style={{ color: '#999', fontSize: '13px' }}>{translations[language]?.noVisibleCameras}</div>
                                        )}
                                        {visibleCameras.map((cam, index) => (
                                            <div key={cam?.id || index} style={{
                                                padding: '12px',
                                                borderRadius: '8px',
                                                border: '1px solid #f0f0f0',
                                                borderLeft: `5px solid ${cam?.is_hidden ? '#bfbfbf' : '#ED2328'}`,
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                backgroundColor: '#fff',
                                                opacity: cam?.is_hidden ? 0.7 : 1
                                            }}>
                                                <div>
                                                    <div style={{ fontWeight: 'bold' }}>
                                                        {cam?.camera_name || `${translations[language]?.camera} ${index + 1}`}
                                                        {cam?.is_hidden && (
                                                            <span style={{ marginLeft: 8, fontSize: '11px', fontWeight: 'normal', color: '#999', border: '1px solid #ddd', borderRadius: 4, padding: '0 6px' }}>
                                                                {translations[language]?.hiddenTag}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div style={{ fontSize: '12px', color: cam?.status === 2 ? '#ff4d4f' : '#52c41a' }}>
                                                        ● {cam?.status === 2 ? translations[language]?.offline : translations[language]?.online}
                                                    </div>
                                                    <div style={{ fontSize: '11px', color: '#999' }}>
                                                        Code: {cam?.camera_index_code}
                                                    </div>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <Tooltip title={cam?.is_hidden ? translations[language]?.unhideCamera : translations[language]?.hideCamera}>
                                                        <Button
                                                            type="text"
                                                            icon={cam?.is_hidden
                                                                ? <EyeOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
                                                                : <EyeInvisibleOutlined style={{ color: '#999', fontSize: '18px' }} />}
                                                            loading={visibilityLoading === cam?.camera_index_code}
                                                            onClick={() => handleToggleCameraVisibility(cam)}
                                                        />
                                                    </Tooltip>
                                                    <Button
                                                        type="text"
                                                        icon={<FaEye style={{ color: '#ED2328', fontSize: '20px' }} />}
                                                        loading={cameraLoading && selectedCamera?.camera_index_code === cam.camera_index_code}
                                                        onClick={() => handleCameraClick(cam)}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    </>
                                        )
                                    })()}
                                </div>
                            )
                        ) : (
                            <div style={{ height: '300px', width: '100%', borderRadius: '12px', overflow: 'hidden', border: '1px solid #ddd' }}>
                                <DeviceMap device={selectedDevice} />
                            </div>
                        )}
                    </div>
                )}
            </Drawer>
            <DeleteModal
                title={translations[language]?.deleteConfirm || "Confirm Delete"}
                bodyText={translations[language]?.deleteDeviceMessage}
                isOpen={isDeleteModalOpen}
                language={language}
                onCancel={() => {
                    setIsDeleteModalOpen(false);
                    setRecordToDelete(null);
                }}
                onDelete={confirmDelete}
            />
        </div>
    )
}

export default Devices
